import React, {Key} from "react";
import {ExportOutlined, MoreOutlined} from "@ant-design/icons";
import {Button, Input, message, Popconfirm, Spin, Tree, Typography} from "antd";
import {IContentTypeStepProps} from "./ContentTypeConfiguration";
import ContentTypesService from "../../../../model/service/dataStorage/ContentTypesService";
import MaintenanceImport from "./maintenace/MaintenaceImport";
import IContentType from "../../../../model/interface/dataStorage/IContentType";
import {FIELD_MODE_RELATION} from "../../../../model/interface/dataStorage/IField";
import LocaleText from "../../settings/dictionary/LocaleText";

interface IState {
    exporting: boolean;
    loadingImport: boolean;
    checked: Key[];
    halfChecked: Key[];
    finalKeys: Key[];
}

interface IProps extends IContentTypeStepProps {
    setupReLoad: () => void;
    setupLoaded: boolean;
}

interface TreeNodeData {
    key: string;
    title: React.ReactNode;
    field: any;
    target: IContentType;
    children: TreeNodeData[];
}

class ContentTypeMaintenance extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            exporting: false,
            loadingImport: false,
            halfChecked: [],
            checked: [],
            finalKeys: []
        };
    }

    exportToZip = (all = true) => {
        const {resource} = this.props;
        this.setState({exporting: true});
        ContentTypesService.exportToZip(resource.uuid, all ? {} : {excludeRelationFields: this.state.finalKeys}).then(() => {
            this.setState({exporting: false});
        }).catch(() => this.setState({exporting: false}));
    };

    onImport = (contentType: IContentType) => {
        message.success(
            <div>
                <div>{`Typ obsahu [${contentType.name}] byl úspěšně importovan. Aktualizuji osobní nastavení.`}</div>
                <div><Spin spinning={true}/></div>
            </div>
        ).then();
        this.props.setupReLoad();
        const promise = new Promise(resolve => {
            if (this.props.setupLoaded) {
                resolve(contentType);
            }
        });
        promise.then(() => {
            message.success('Aktualizace dokončena').then();
            this.props.history.push('/app/configuration/content-types/' + contentType.id);
        });
    };

    // Builds a pure JavaScript Tree Object array
    getTreeData = (rootContentType: IContentType): TreeNodeData[] => {
        const {contentTypes} = this.props;
        const {finalKeys} = this.state;
        const visited = new Set<string>();

        const getRelations = (ct: IContentType) =>
            ct.fields
                .filter(f => f.mode === FIELD_MODE_RELATION)
                .map(f => ({
                    field: f,
                    target: contentTypes.find(c => c.fullClassName === f.targetEntity)
                }))
                .filter((item): item is { field: any; target: IContentType } => Boolean(item.target));

        const buildNodes = (ct: IContentType, rootCts?: string[]): TreeNodeData[] => {
            const relations = getRelations(ct);
            const currentLevelNodes: TreeNodeData[] = [];
            rootCts?.push(ct.uuid)

            for (const {field, target} of relations) {
                if (visited.has(field.uuid) || rootCts?.includes(target.uuid)) {
                    continue;
                }
                visited.add(field.uuid);

                currentLevelNodes.push({
                    key: field.uuid,
                    title: (
                        <Typography.Text delete={finalKeys.includes(field.uuid)}>
                            <LocaleText code={field.label || ''}/> [{field.name}] - <LocaleText
                            code={target.label || ''}/> [{target.name}]
                        </Typography.Text>
                    ),
                    field,
                    target,
                    children: []
                });
            }

            for (const node of currentLevelNodes) {
                if (rootContentType.uuid === ct.uuid){
                    rootCts = [rootContentType.uuid]
                }
                node.children = buildNodes(node.target, rootCts);
            }

            return currentLevelNodes;
        };

        return buildNodes(rootContentType);
    };

    // Helper: Find a target node inside pure tree array by key
    findNodeByKey = (nodes: TreeNodeData[], targetKey: string): TreeNodeData | null => {
        for (const node of nodes) {
            if (node.key === targetKey) return node;
            if (node.children && node.children.length > 0) {
                const found = this.findNodeByKey(node.children, targetKey);
                if (found) return found;
            }
        }
        return null;
    };

    // Helper: Collect all descendant keys from pure data node
    getAllDescendantKeys = (node: TreeNodeData): string[] => {
        let keys: string[] = [];
        if (node.children && node.children.length > 0) {
            for (const child of node.children) {
                keys.push(child.key);
                keys = keys.concat(this.getAllDescendantKeys(child));
            }
        }
        return keys;
    };

    handleCheck = (checkedObj: any, info: any) => {
        const {resource} = this.props;

        // 1. Get raw keys currently checked by checkStrictly
        const rawCheckedKeys = Array.isArray(checkedObj) ? checkedObj : checkedObj.checked;
        const currentCheckedSet = new Set<string>(rawCheckedKeys.map(String));

        const clickedKey = String(info.node.key);

        // 2. Fetch raw tree structure and locate clicked node
        const treeData = this.getTreeData(resource);
        const clickedDataNode = this.findNodeByKey(treeData, clickedKey);

        if (clickedDataNode) {
            const childKeys = this.getAllDescendantKeys(clickedDataNode);

            if (info.checked) {
                // Check parent -> add ALL descendant child keys
                currentCheckedSet.add(clickedKey);
                childKeys.forEach(k => currentCheckedSet.add(k));
            } else {
                // Uncheck parent -> remove ALL descendant child keys
                currentCheckedSet.delete(clickedKey);
                childKeys.forEach(k => currentCheckedSet.delete(k));
            }
        }

        const finalKeys = Array.from(currentCheckedSet);

        // 3. Update State
        this.setState({checked: finalKeys, finalKeys});
    };

    render() {
        const {exporting, checked} = this.state;
        const {resource, history, match} = this.props;
        const treeData = this.getTreeData(resource);

        return (
            <>
                <Input.Group compact={true}>
                    <Button disabled={!resource.id} type={"primary"} loading={exporting} icon={<ExportOutlined/>}
                            onClick={() => this.exportToZip}>
                        <LocaleText code={'configuration.content-type.maintenance.export'} fallback={'Exportovat'}/>
                    </Button>
                    {resource.fields.some(f => f.mode === FIELD_MODE_RELATION) && (
                        <Popconfirm onConfirm={() => this.exportToZip(false)}
                                    okButtonProps={{icon: <ExportOutlined/>}}
                                    okText={<LocaleText code={'configuration.content-type.maintenance.export'} fallback={'Exportovat'}/>}
                                    title={<div>
                            <LocaleText code={'configuration.content-type.maintenance.export-exclude-fields'}
                                        fallback={'Vynechat vazebné pole'}/>
                            <div className={'mt-3'}>
                                <Tree
                                    checkable={true}
                                    checkStrictly={true}
                                    checkedKeys={checked}
                                    onCheck={this.handleCheck}
                                    defaultExpandAll={true}
                                    treeData={treeData}
                                />
                            </div>

                        </div>}>
                            <Button disabled={!resource.id} type={"default"} icon={<MoreOutlined/>}>
                            </Button>
                        </Popconfirm>
                    )}
                </Input.Group>

                <div className={'mt-3'}>
                    <MaintenanceImport onImport={this.onImport} resource={resource} history={history} match={match}/>
                </div>
            </>
        );
    }
}

export default ContentTypeMaintenance;